import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { Histogram, MetricsResult } from "../engine/types";
import {
  chartColors,
  chartThemes,
  type ChartTheme,
  featureColor,
} from "../theme";
import { useTheme } from "../themeContext";
import {
  bonusBreakdownData,
  featureTriggerBars,
  percentContributionBars,
  type PercentBarDatum,
  perFeatureAwardHistogramData,
  winDistributionData,
  type WinBucketDatum,
} from "./charts";

/**
 * Headline charts (Recharts), all driven by the same `MetricsResult` the
 * panel above renders — so they converge live during streaming and snap
 * to the authoritative values on completion. Animation is disabled so
 * bars don't re-tween on every streaming partial. Colors + tooltip/grid
 * styling come from `src/theme.ts` (single source of truth), resolved
 * per light/dark mode via `useTheme()`.
 *
 * - **Win distribution** — the 15 log-scaled `histogram` buckets.
 * - **RTP by symbol / by rule** — `per_symbol_contribution` /
 *   `rtp_per_rule` as sorted horizontal %-contribution bars.
 * - **Feature triggers** — `feature_triggers` rates.
 * - **Bonus split** — base vs bonus RTP as one stacked bar.
 */
export function ChartsPanel({
  metrics,
  streaming = false,
}: {
  metrics: MetricsResult;
  streaming?: boolean;
}) {
  const { theme } = useTheme();
  const ct = chartThemes[theme];
  const distribution = winDistributionData(metrics.histogram);
  const symbols = percentContributionBars(metrics.per_symbol_contribution);
  const rules = percentContributionBars(metrics.rtp_per_rule);
  const features = featureTriggerBars(metrics.feature_triggers);
  const breakdown = bonusBreakdownData(
    metrics.rtp?.point,
    metrics.bonus_contribution,
  );
  const bonusTotalPct = breakdown.features.reduce((s, f) => s + f.pct, 0);

  return (
    <section className="border border-paper-border p-4 dark:border-term-border">
      <header className="mb-4 flex items-center gap-2">
        <h2 className="text-sm font-bold uppercase tracking-widest text-paper-bright dark:text-term-bright">
          Charts
        </h2>
        {streaming && (
          <span className="inline-flex items-center gap-1.5 border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
            streaming
          </span>
        )}
      </header>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Chart
          title="Win distribution"
          subtitle="spins per win-multiplier bucket (log)"
        >
          {distribution.length > 0 ? (
            <LogCountBarChart
              data={distribution}
              ct={ct}
              tooltipLabel="spins"
            />
          ) : (
            <Empty />
          )}
        </Chart>

        <PerFeatureAwardChart
          histograms={metrics.per_feature_award_histogram ?? {}}
          ct={ct}
        />

        <PercentBarChart
          title="RTP by symbol"
          subtitle="contribution to return (%)"
          data={symbols}
          color={ct.indigo}
          ct={ct}
        />

        <PercentBarChart
          title="RTP by rule"
          subtitle="top contributing pay rules (%)"
          data={rules}
          color={chartColors.sky}
          ct={ct}
        />

        <PercentBarChart
          title="Feature triggers"
          subtitle="trigger rate per spin (%)"
          data={features}
          color={chartColors.pink}
          ct={ct}
          valueLabel="rate"
          decimals={4}
        />

        <div className="lg:col-span-2">
          <Chart
            title="Bonus split"
            subtitle="base vs per-feature bonus contribution to RTP (%)"
          >
            {breakdown.base + bonusTotalPct > 0 ? (
              <>
                <div className="flex h-4 w-full overflow-hidden">
                  <div
                    style={{
                      width: `${(breakdown.base / (breakdown.base + bonusTotalPct)) * 100}%`,
                      backgroundColor: chartColors.emerald,
                    }}
                    title={`Base ${breakdown.base.toFixed(2)}%`}
                  />
                  {breakdown.features.map((f, i) => (
                    <div
                      key={f.name}
                      style={{
                        width: `${(f.pct / (breakdown.base + bonusTotalPct)) * 100}%`,
                        backgroundColor: featureColor(i),
                      }}
                      title={`${f.name} ${f.pct.toFixed(2)}%`}
                    />
                  ))}
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-paper-label dark:text-term-label">
                  <LegendDot
                    color={chartColors.emerald}
                    label={`Base ${breakdown.base.toFixed(2)}%`}
                  />
                  {breakdown.features.map((f, i) => (
                    <LegendDot
                      key={f.name}
                      color={featureColor(i)}
                      label={`${f.name} ${f.pct.toFixed(2)}%`}
                    />
                  ))}
                </div>
              </>
            ) : (
              <Empty />
            )}
          </Chart>
        </div>
      </div>
    </section>
  );
}

/** Sorted horizontal %-bar — shared by symbol / rule / feature charts. */
function PercentBarChart({
  title,
  subtitle,
  data,
  color,
  ct,
  valueLabel = "RTP",
  decimals = 3,
}: {
  title: string;
  subtitle: string;
  data: PercentBarDatum[];
  color: string;
  ct: ChartTheme;
  valueLabel?: string;
  decimals?: number;
}) {
  return (
    <Chart title={title} subtitle={subtitle}>
      {data.length > 0 ? (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 8, right: 12, bottom: 4, left: 4 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={ct.grid}
              horizontal={false}
            />
            <XAxis
              type="number"
              tick={ct.tick}
              axisLine={{ stroke: ct.axis }}
              tickLine={{ stroke: ct.axis }}
              tickFormatter={(v: number) => `${v}%`}
            />
            <YAxis
              type="category"
              dataKey="label"
              width={72}
              tick={ct.tick}
              axisLine={{ stroke: ct.axis }}
              tickLine={{ stroke: ct.axis }}
            />
            <Tooltip
              formatter={(value) => [
                typeof value === "number"
                  ? `${value.toFixed(decimals)}%`
                  : String(value),
                valueLabel,
              ]}
              contentStyle={ct.tooltipContentStyle}
              labelStyle={ct.tooltipLabelStyle}
              itemStyle={ct.tooltipItemStyle}
              cursor={{ fill: ct.cursorFill }}
            />
            <Bar dataKey="pct" fill={color} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Empty />
      )}
    </Chart>
  );
}

/** Shared log-Y count BarChart (the "win distribution" shape). Used by
 *  both the global win-distribution chart and the per-feature award
 *  chart, which bin into the identical 15-bucket log scale. */
function LogCountBarChart({
  data,
  ct,
  tooltipLabel,
}: {
  data: WinBucketDatum[];
  ct: ChartTheme;
  tooltipLabel: string;
}) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={ct.grid}
          vertical={false}
        />
        <XAxis
          dataKey="label"
          angle={-45}
          textAnchor="end"
          interval={0}
          height={52}
          tick={ct.tick}
          axisLine={{ stroke: ct.axis }}
          tickLine={{ stroke: ct.axis }}
        />
        <YAxis
          scale="log"
          domain={[0.9, "dataMax"]}
          allowDataOverflow
          width={48}
          tick={ct.tick}
          axisLine={{ stroke: ct.axis }}
          tickLine={{ stroke: ct.axis }}
          tickFormatter={formatAxisCount}
        />
        <Tooltip
          formatter={(value) => [
            typeof value === "number" ? value.toLocaleString() : "0",
            tooltipLabel,
          ]}
          contentStyle={ct.tooltipContentStyle}
          labelStyle={ct.tooltipLabelStyle}
          itemStyle={ct.tooltipItemStyle}
          cursor={{ fill: ct.cursorFill }}
        />
        <Bar
          dataKey="count"
          fill={chartColors.emerald}
          isAnimationActive={false}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Spec 013 — "win distribution by feature": the per-feature award
 * histogram with a feature-picker. Each option is a feature that paid ≥1
 * award (`Object.keys(per_feature_award_histogram)`); the selected
 * feature's histogram renders through the shared log-Y chart. The
 * selection is derived (not synced via effect) so a feature key that
 * disappears between streaming partials falls back to the first
 * available. Empty-state when no feature paid an award.
 */
function PerFeatureAwardChart({
  histograms,
  ct,
}: {
  histograms: Record<string, Histogram>;
  ct: ChartTheme;
}) {
  const features = Object.keys(histograms).sort();
  const [selected, setSelected] = useState("");
  const effective =
    selected && features.includes(selected) ? selected : (features[0] ?? "");
  const data: WinBucketDatum[] = perFeatureAwardHistogramData(
    effective ? histograms[effective] : undefined,
  );
  return (
    <Chart
      title="Win distribution by feature"
      subtitle="award events per amount bucket (log)"
    >
      {features.length > 0 ? (
        <div>
          <select
            aria-label="Select feature"
            value={effective}
            onChange={(e) => setSelected(e.target.value)}
            className="mb-2 w-full border border-paper-border bg-transparent px-2 py-1 text-[11px] text-paper-text dark:border-term-border dark:text-term-text"
          >
            {features.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
          <LogCountBarChart data={data} ct={ct} tooltipLabel="award events" />
        </div>
      ) : (
        <Empty label="No per-feature awards" />
      )}
    </Chart>
  );
}

function Chart({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="text-[11px] font-bold uppercase tracking-widest text-paper-text dark:text-term-text">
        {title}
      </h3>
      <p className="mb-1 text-[10px] text-paper-hint dark:text-term-hint">
        {subtitle}
      </p>
      {children}
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

function Empty({ label = "No data yet" }: { label?: string }) {
  return (
    <div className="flex h-[220px] items-center justify-center text-xs text-paper-hint dark:text-term-hint">
      {label}
    </div>
  );
}

/** Compact log-axis tick: 1, 10, 1k, 1M. */
function formatAxisCount(v: number): string {
  if (v >= 1_000_000) return `${v / 1_000_000}M`;
  if (v >= 1_000) return `${v / 1_000}k`;
  return `${v}`;
}
